import classNames from 'volta/helpers/class-names';
import BvBtn from 'volta/components/bv-btn';
import BvIcon from 'volta/components/bv-icon';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { TFlashMessageType } from 'volta/models/bv-flash';
import type BvFlashInstance from 'volta/models/bv-flash';
import type BvFlashService from 'volta/services/bv-flash';

const TYPES: TFlashMessageType[] = ['info', 'success', 'warning', 'error'];

interface BvFlashSignature {
  Args: {
    flash: BvFlashInstance;
  };
  Element: HTMLDivElement;
}

export default class BvFlashComponent extends Component<BvFlashSignature> {
  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class={{classNames 'bv-flash' (if @flash.onClick 'bv-flash--clickable') this.processedType}}
      ...attributes
      {{on 'mouseup' this.handleMouseDown}}
      {{on 'mouseenter' this.handleMouseEnter}}
      {{on 'mouseleave' this.handleMouseLeave}}
    >
      <div class='bv-flash__wrapper'>
        <div class='bv-flash__icon'>
          <BvIcon @glyph={{this.flashIcon}} @hasBackdrop={{true}} @color={{this.iconColor}} />
        </div>
        <div class='bv-flash__content'>
          <div class='bv-flash__message'>
            {{#if @flash.htmlContent}}
              {{! template-lint-disable no-triple-curlies }}{{{@flash.message}}}
            {{else}}
              {{@flash.message}}
            {{/if}}
          </div>
          <BvBtn
            class='bv-flash__close'
            @subtle={{true}}
            @size='sm'
            @iconLeft='close'
            @rounded={{true}}
            @onClick={{this.handleRemoveFlash}}
          />
        </div>

      </div>
    </div>
  </template>
  @service declare bvFlash: BvFlashService;

  @tracked paused = false;

  // Set icon depending on notification type
  get flashIcon() {
    const { flash } = this.args;

    switch (flash.type) {
      case 'info': {
        return 'information';
      }
      case 'success': {
        return 'checkmark';
      }
      case 'warning': {
        return 'alert';
      }
      case 'error': {
        return 'close';
      }
    }
  }

  get iconColor() {
    return this.args.flash.type === 'error' ? 'danger' : this.args.flash.type;
  }

  get processedType() {
    const { flash } = this.args;
    return flash && flash.type && TYPES.includes(flash.type)
      ? `bv-flash--${flash.type}`
      : undefined;
  }

  @action
  handleRemoveFlash() {
    this.bvFlash.remove(this.args.flash);
  }

  @action
  handleMouseDown(_e: MouseEvent) {
    const { flash } = this.args;
    flash.onClick?.(flash);
  }

  @action
  handleMouseEnter() {
    const { flash } = this.args;
    if (flash.autoClear) {
      this.paused = true;
      this.bvFlash.pauseAutoClear(flash);
    }
  }

  @action
  handleMouseLeave() {
    const { flash } = this.args;
    if (flash.autoClear) {
      this.paused = false;
      this.bvFlash.setupAutoClear(flash);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvFlash: typeof BvFlashComponent;
    'bv-flash': typeof BvFlashComponent;
  }
}
